import { ReactSketchCanvasRef } from 'react-sketch-canvas';
import { ImageDimensions } from './imageEditConstants';

export const exportCanvasDrawing = async (
  canvasRef: ReactSketchCanvasRef,
  displayDimensions: ImageDimensions,
  strokeColor: string,
  strokeWidth: number
): Promise<string> => {
  console.log('Starting canvas export...');
  
  try {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const exportResult = await canvasRef.exportImage('png');
    if (!exportResult) {
      throw new Error('Drawing export returned null/undefined');
    }
    return exportResult;
  } catch (exportError) {
    
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const retryExport = await canvasRef.exportImage('png');
      if (retryExport) {
        return retryExport;
      } else {
        throw new Error('Retry export also returned null');
      }
    } catch (retryError) {
      
      try {
        const svgData = await canvasRef.exportSvg();
        if (svgData) {
          return await convertSvgToPng(svgData, displayDimensions);
        } else {
          throw new Error('SVG export returned null or empty');
        }
      } catch (svgError) {
        console.error('SVG fallback failed:', svgError);
        
        try {
          console.log('Fallback 3: Trying paths export...');
          const paths = await canvasRef.exportPaths();
          
          if (paths && paths.length > 0) {
            console.log('Creating manual canvas from paths...');
            return createCanvasFromPaths(paths, displayDimensions, strokeColor, strokeWidth);
          } else {
            console.log('No paths found, creating empty transparent canvas...');
            return createEmptyCanvas(displayDimensions);
          }
        } catch (pathsError) {
          console.error('Paths fallback also failed:', pathsError);
          throw new Error('All fallback methods failed. Original error: ' + (exportError instanceof Error ? exportError.message : String(exportError)));
        }
      }
    }
  }
};

const convertSvgToPng = async (svgData: string, dimensions: ImageDimensions): Promise<string> => {
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml' });
  const svgUrl = URL.createObjectURL(svgBlob);
  
  try {
    const svgImg = new Image();
    const pngDataUrl = await new Promise<string>((resolve, reject) => {
      svgImg.onload = () => {
        console.log('SVG image loaded for conversion');
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = dimensions.width;
        tempCanvas.height = dimensions.height;
        const tempCtx = tempCanvas.getContext('2d');
        if (tempCtx) {
          tempCtx.clearRect(0, 0, dimensions.width, dimensions.height);
          tempCtx.drawImage(svgImg, 0, 0, dimensions.width, dimensions.height);
          const result = tempCanvas.toDataURL('image/png');
          console.log('SVG->PNG conversion successful');
          resolve(result);
        } else {
          reject(new Error('Could not create temp canvas context'));
        }
      };
      svgImg.onerror = (err) => {
        console.error('SVG image loading failed:', err);
        reject(new Error('SVG loading failed'));
      };
      svgImg.src = svgUrl;
    });
    
    return pngDataUrl;
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
};

const createCanvasFromPaths = (
  paths: any[],
  dimensions: ImageDimensions,
  strokeColor: string,
  strokeWidth: number
): string => {
  const manualCanvas = document.createElement('canvas');
  manualCanvas.width = dimensions.width;
  manualCanvas.height = dimensions.height;
  const manualCtx = manualCanvas.getContext('2d');
  
  if (!manualCtx) {
    throw new Error('Could not create manual canvas context');
  }
  
  manualCtx.clearRect(0, 0, dimensions.width, dimensions.height);
  
  paths.forEach((path: any) => {
    if (path.paths && path.paths.length > 0) {
      manualCtx.strokeStyle = path.strokeColor || strokeColor;
      manualCtx.lineWidth = path.strokeWidth || strokeWidth;
      manualCtx.lineCap = 'round';
      manualCtx.lineJoin = 'round';
      
      manualCtx.beginPath();
      path.paths.forEach((point: any, pointIndex: number) => {
        if (pointIndex === 0) {
          manualCtx.moveTo(point.x, point.y);
        } else {
          manualCtx.lineTo(point.x, point.y);
        }
      });
      manualCtx.stroke();
    }
  });
  
  console.log('Manual canvas recreation successful!');
  return manualCanvas.toDataURL('image/png');
};

const createEmptyCanvas = (dimensions: ImageDimensions): string => {
  const emptyCanvas = document.createElement('canvas');
  emptyCanvas.width = dimensions.width;
  emptyCanvas.height = dimensions.height;
  const emptyCtx = emptyCanvas.getContext('2d');
  
  if (!emptyCtx) {
    throw new Error('Could not create empty canvas context');
  }
  
  emptyCtx.clearRect(0, 0, dimensions.width, dimensions.height);
  console.log('Empty transparent canvas created successfully');
  return emptyCanvas.toDataURL('image/png');
};

export const combineImageWithDrawing = async (
  backgroundImage: string,
  drawingImageData: string,
  imageDimensions: ImageDimensions,
  displayDimensions: ImageDimensions
): Promise<Blob> => {
  const tempCanvas = document.createElement('canvas');
  const tempCtx = tempCanvas.getContext('2d');

  if (!tempCtx) {
    throw new Error('Canvas Context konnte nicht erstellt werden');
  }

  tempCanvas.width = imageDimensions.width;
  tempCanvas.height = imageDimensions.height;

  // Background image laden
  const backgroundImg = new Image();
  const isBlobUrl = backgroundImage.startsWith('blob:');
  
  if (!isBlobUrl) {
    backgroundImg.crossOrigin = 'anonymous';
  }

  await new Promise((resolve, reject) => {
    backgroundImg.onload = () => {
      console.log('Background image loaded successfully');
      resolve(null);
    };
    backgroundImg.onerror = (error) => {
      console.error('Failed to load background image:', error);
      console.log('Retrying image load without crossOrigin...');
      const retryImg = new Image();
      retryImg.onload = () => {
        console.log('Retry successful without crossOrigin');
        resolve(null);
      };
      retryImg.onerror = (retryError) => {
        console.error('Retry without crossOrigin also failed:', retryError);
        reject(new Error('Image loading failed even without crossOrigin'));
      };
      retryImg.src = backgroundImage;
    };
    backgroundImg.src = backgroundImage;
  });

  tempCtx.drawImage(backgroundImg, 0, 0, imageDimensions.width, imageDimensions.height);
  console.log('Background image drawn to canvas');

  // Drawing image laden
  const drawingImg = new Image();
  await new Promise((resolve, reject) => {
    drawingImg.onload = () => {
      console.log('Drawing image loaded successfully');
      resolve(null);
    };
    drawingImg.onerror = (error) => {
      console.error('Failed to load drawing image:', error);
      reject(new Error('Drawing image loading failed'));
    };
    drawingImg.src = drawingImageData;
  });

  const scaleX = imageDimensions.width / displayDimensions.width;
  const scaleY = imageDimensions.height / displayDimensions.height;
  
  console.log('Scale factors:', { scaleX, scaleY });
  
  tempCtx.drawImage(
    drawingImg, 
    0, 0, displayDimensions.width, displayDimensions.height,
    0, 0, imageDimensions.width, imageDimensions.height
  );
  
  console.log('Drawing overlaid on background');

  return new Promise((resolve, reject) => {
    tempCanvas.toBlob((blob) => {
      if (blob) {
        console.log('Combined image blob created:', { size: blob.size, type: blob.type });
        resolve(blob);
      } else {
        reject(new Error('Failed to create combined image blob'));
      }
    }, 'image/png');
  });
};

export const convertDataUrlToBlob = async (imageData: string): Promise<Blob> => {
  try {
    const response = await fetch(imageData);
    if (!response.ok) {
      throw new Error(`Fetch failed with status: ${response.status}`);
    }
    const blob = await response.blob();
    return blob;
  } catch (fetchError) {
    try {
      const base64Data = imageData.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });

      return blob;
    } catch (manualError) {
      throw new Error('Failed to create blob from image data');
    }
  }
};
